import React from 'react';
import { AlertTriangle, ArrowRight, DollarSign, Gift, X } from 'lucide-react';

const DependencyPrompt = ({ pendingChanges, onConfirm, onCancel }) => {
  if (!pendingChanges) return null;

  const { type, oldValue, newValue, impacts } = pendingChanges;

  // Calculate net impact
  const lostValue = impacts
    .filter(i => i.type === 'LOSE_PERK' || i.type === 'LOSE_CREDIT' || i.type === 'LOSE_MEMBERSHIP_PERK')
    .reduce((sum, i) => sum + (i.monthlyCost || i.amount || 0), 0);

  const gainedValue = impacts
    .filter(i => i.type === 'GAIN_PERK' || i.type === 'UPGRADE_TO_FREE')
    .reduce((sum, i) => sum + (i.monthlySavings || 0), 0);

  const netImpact = gainedValue - lostValue;

  const getTitle = () => {
    switch (type) {
      case 'ISP_CHANGE':
        return 'Change Internet Provider?';
      case 'REMOVE_CARD':
        return 'Remove Credit Card?';
      case 'REMOVE_MEMBERSHIP':
        return 'Remove Membership?';
      default:
        return 'Confirm Change?';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'ISP_CHANGE':
        return `Switching from ${oldValue} to ${newValue} will affect your streaming perks.`;
      case 'REMOVE_CARD':
        return 'Removing this card will affect your credit allocations.';
      case 'REMOVE_MEMBERSHIP':
        return 'Removing this membership will affect your included services.';
      default:
        return 'This change will affect your subscriptions.';
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact.type) {
      case 'LOSE_PERK':
      case 'LOSE_CREDIT':
      case 'LOSE_MEMBERSHIP_PERK':
        return <X className="w-4 h-4 text-red-500" />;
      case 'GAIN_PERK':
      case 'UPGRADE_TO_FREE':
        return <Gift className="w-4 h-4 text-green-500" />;
      default:
        return <ArrowRight className="w-4 h-4 text-gray-500" />;
    }
  };

  const getImpactClass = (impact) => {
    switch (impact.type) {
      case 'LOSE_PERK':
      case 'LOSE_CREDIT':
      case 'LOSE_MEMBERSHIP_PERK':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'GAIN_PERK':
      case 'UPGRADE_TO_FREE':
        return 'bg-green-50 border-green-200 text-green-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 bg-yellow-100 rounded-full">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{getTitle()}</h3>
            <p className="text-sm text-gray-600">{getDescription()}</p>
          </div>
        </div>

        {/* Impact List */}
        <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
          <p className="text-sm font-medium text-gray-700 mb-3">
            This change will have the following effects:
          </p>
          
          <div className="space-y-2">
            {impacts.map((impact, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 p-3 rounded-lg border ${getImpactClass(impact)}`}
              >
                <div className="mt-0.5">{getImpactIcon(impact)}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{impact.message}</p>
                  {(impact.monthlyCost > 0 || impact.amount > 0) && (
                    <p className="text-xs mt-1 opacity-75">
                      ${(impact.monthlyCost || impact.amount).toFixed(2)}/month
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Net Impact Summary */}
          {(lostValue > 0 || gainedValue > 0) && (
            <div className={`mt-4 p-4 rounded-lg border-2 ${
              netImpact >= 0 
                ? 'bg-green-50 border-green-300' 
                : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className={`w-5 h-5 ${
                  netImpact >= 0 ? 'text-green-600' : 'text-red-600'
                }`} />
                <span className="font-semibold text-gray-900">Net Impact</span>
              </div>
              
              <div className="space-y-1 text-sm">
                {lostValue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Value lost:</span>
                    <span className="text-red-600 font-medium">-${lostValue.toFixed(2)}/mo</span>
                  </div>
                )}
                {gainedValue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Value gained:</span>
                    <span className="text-green-600 font-medium">+${gainedValue.toFixed(2)}/mo</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                  <span className="font-semibold text-gray-900">Net change:</span>
                  <span className={`font-bold ${
                    netImpact >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {netImpact >= 0 ? '+' : ''}{netImpact.toFixed(2)}/mo
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 font-medium rounded-lg transition-colors ${
              netImpact < 0
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {netImpact < 0 ? 'Proceed Anyway' : 'Confirm Change'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DependencyPrompt;